import { useMemo } from "react";
import { useSelector } from "react-redux";

import { usePracticeTeamMembers } from "../../hooks/usePracticeTeamMembers";
import { lawyerSelectors } from "../../redux/lawyer/selectors";
import type { TeamMember } from "../caseIde/chat/mentions";

/**
 * The firm's members for @-mentions, plus who "me" is. Shared by the Case IDE
 * pane and the agent panel. Reads the practice from
 * the signed-in lawyer; the list itself comes from `usePracticeTeamMembers`.
 */
export const useTeamMembers = (): {
  members: TeamMember[];
  me: { uid: string; name: string; email: string; avatarUrl: string | null };
  loading: boolean;
} => {
  const practice = useSelector(lawyerSelectors.selectPracticeName);
  const uid = useSelector(lawyerSelectors.selectUid);
  const fullName = useSelector(lawyerSelectors.selectFullName);
  const email = useSelector(lawyerSelectors.selectEmail);
  const avatarSrc = useSelector(lawyerSelectors.selectAvatarSrc);
  const { options, isLoading } = usePracticeTeamMembers(practice);

  const members = useMemo<TeamMember[]>(
    () =>
      options.map((o) => ({
        uid: o.uid,
        label: o.label,
        email: o.email,
        avatarUrl: null
      })),
    [options]
  );
  const me = useMemo(
    () => ({
      uid: uid ?? "",
      name: (fullName ?? "").trim() || email || "",
      email: email ?? "",
      avatarUrl: avatarSrc || null
    }),
    [uid, fullName, email, avatarSrc]
  );
  return { members, me, loading: isLoading };
};
